import { useContext } from 'react';

import { useId } from './useId.js';
import {
  type SharedInputProps,
  SharedInputPropsContext,
} from '../contexts/SharedInputPropsContext.js';

/**
 * Whenever the component is rendered as wrapped within a `SharedInputPropsContext` provider, the hook will
 * return the context values for the `id`, `disabled`, `required` and `labelId` props defined on a context level. Otherwise
 * the component's very own property values are used as fallback, as passed through the hook payload.
 * @param defaultProps - Component very own `id`, `required` and `disabled` prop values, if any.
 * If the component is NOT rendered within a `SharedInputPropsContext` provider, the hook will return these fallback props as default.
 * @see {@link SharedInputPropsContext}
 * @see {@link SharedInputProps}
 * @internal
 */
export function useSharedInputPropsContext(defaultProps: SharedInputProps): {
  id: string;
  disabled: boolean;
  required: boolean;
  labelId?: string;
} {
  const uniqueId = useId();
  const context = useContext(SharedInputPropsContext);

  const id =
    context.id || defaultProps.id || context.generatedUniqueId || uniqueId;
  const disabled = context.disabled || defaultProps.disabled || false;
  const required = context.required || defaultProps.required || false;
  const labelId = context.generatedUniqueId || defaultProps.labelId;

  return { id, disabled, required, labelId };
}
